<template>
  <section class="chart-statistics-container">
    <el-container class="chart-statistics package-title">
      <el-main>
        <h1>{{ $t('packagesTile.title') }}</h1>
      </el-main>
    </el-container>

    <div class="chart-statistics">
      <TileRowDivider
        :column-label01="$t('packagesTile.packageType.hgv')"
        :column-label02="$t('packagesTile.packageType.libert')"
        :column-label03="$t('packagesTile.packageType.obu')"
      />

      <!------------------------- AS24 START ------------------------->

      <div class="chart-statistic-row">
        <div class="logo-box">
          <el-image :src="as24Logo" />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.as24.hgv.completed, packageStatistics.as24.hgv.all)"
            :series="packageDonutSeries.as24Hgv"
          />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.as24.libert.completed, packageStatistics.as24.libert.all)"
            :series="packageDonutSeries.as24Libert"
          />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.as24.obu.completed, packageStatistics.as24.obu.all)"
            :series="packageDonutSeries.as24Obu"
          />
        </div>
      </div>

      <!-------------------------- AS24 END -------------------------->

      <TileRowDivider />

      <!------------------------- TOTAL START ------------------------->

      <div class="chart-statistic-row">
        <div class="logo-box">
          <el-image :src="totalLogo" />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.total_energies.hgv.completed, packageStatistics.total_energies.hgv.all)"
            :series="packageDonutSeries.totalHgv"
          />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.total_energies.libert.completed, packageStatistics.total_energies.libert.all)"
            :series="packageDonutSeries.totalLibert"
          />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.total_energies.obu.completed, packageStatistics.total_energies.obu.all)"
            :series="packageDonutSeries.totalObu"
          />
        </div>
      </div>

      <!------------------------- TOTAL END ------------------------->

      <TileRowDivider />

      <!------------------------- CIRCLE-K START ------------------------->

      <div class="chart-statistic-row">
        <div class="logo-box">
          <el-image :src="circlekLogo" />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.circle_k.hgv.completed, packageStatistics.circle_k.hgv.all)"
            :series="packageDonutSeries.circlekHgv"
            style="height: auto;"
          />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.circle_k.libert.completed, packageStatistics.circle_k.libert.all)"
            :series="packageDonutSeries.circlekLibert"
          />
        </div>

        <div
          v-loading="packagesTileStatisticsLoading"
          class="chart-container"
        >
          <apexchart
            :options="getDonutOptions(packageStatistics.circle_k.obu.completed, packageStatistics.circle_k.obu.all)"
            :series="packageDonutSeries.circlekObu"
          />
        </div>
      </div>

      <!------------------------- CIRCLE-K END ------------------------->

      <TileRowDivider />

      <!------------------------- RESTANTS START ------------------------->

      <div class="bottom-info-container">
        <div class="logo-box">
          <span>{{ $t('Remaining badges') }}</span>
        </div>

        <div class="bottom-info">
          <el-tag
            v-loading="packagesTileStatisticsLoading"
            effect="dark"
          >
            <span>
              {{ packageItemStatistics.hgv > 100 ? '> 100' : packageItemStatistics.hgv }}
            </span>
          </el-tag>
        </div>

        <div class="bottom-info">
          <el-tag
            v-loading="packagesTileStatisticsLoading"
            effect="dark"
          >
            <span>
              {{ packageItemStatistics.libert > 100 ? '> 100' : packageItemStatistics.libert }}
            </span>
          </el-tag>
        </div>

        <div class="bottom-info">
          <el-tag
            v-loading="packagesTileStatisticsLoading"
            effect="dark"
          >
            <span>
              {{ packageItemStatistics.obu > 100 ? '> 100' : packageItemStatistics.obu }}
            </span>
          </el-tag>
        </div>
      </div>
    </div>

    <!------------------------- RESTANTS END ------------------------->
  </section>
</template>

<script lang="ts">
import { Vue } from 'vue-property-decorator'
import Component from 'vue-class-component'

import {
  PackagesTilePackageInfo,
  PackagesTileStatisticsQuery,
  PackagesTilePackageStatistics,
  PackagesTilePackageItemStatistics

} from '@/types/graphql'
import { ApolloQueryResult } from 'apollo-client'
import TileRowDivider from '@/components/UI/TileRowDivider.vue'

// ⁡⁢⁣⁣⁡⁢⁣⁣⓿ 𝗧𝘆𝗽𝗲𝘀⁡

@Component({
  components: {
    TileRowDivider
  }
})

export default class PackagesTile extends Vue {
  // ⁡⁢⁣⁣➊ 𝗣𝗿𝗼𝗽𝘀⁡

  // ⁡⁢⁣⁣⁡⁢⁣⁣➋ 𝗗𝗮𝘁𝗮⁡

  private as24Logo = require('@/assets/AS24_tile_logo.png');

  private totalLogo = require('@/assets/TotalEnergies_tile_logo.png');

  private circlekLogo = require('@/assets/circlek_logo.jpg');

  private packagesTileStatisticsLoading = false;

  private packageStatistics: PackagesTilePackageStatistics = {
    as24: {
      hgv: { completed: 0, all: -1 },
      libert: { completed: 0, all: -1 },
      obu: { completed: 0, all: -1 }
    },
    total_energies: {
      hgv: { completed: 0, all: -1 },
      libert: { completed: 0, all: -1 },
      obu: { completed: 0, all: -1 }
    },
    circle_k: {
      hgv: { completed: 0, all: -1 },
      libert: { completed: 0, all: -1 },
      obu: { completed: 0, all: -1 }
    }
  }

  private packageItemStatistics: PackagesTilePackageItemStatistics = {
    hgv: 0,
    libert: 0,
    obu: 0
  }

  // ⁡⁢⁣⁣⁡⁢⁣⁣➌ 𝗖𝗼𝗺𝗽𝘂𝘁𝗲𝗱⁡

  private get packageDonutSeries() {
    const calculatePercentage = (packageInfo: PackagesTilePackageInfo): number[] => {
      if (packageInfo.all === 0) {
        return [0, 100]
      }

      const completedPercentage = Math.round(Math.min(Math.max(packageInfo.completed / packageInfo.all, 0), 1) * 100)

      return [completedPercentage, 100 - completedPercentage]
    }

    return {
      as24Hgv: calculatePercentage(this.packageStatistics.as24.hgv),
      as24Libert: calculatePercentage(this.packageStatistics.as24.libert),
      as24Obu: calculatePercentage(this.packageStatistics.as24.obu),
      totalHgv: calculatePercentage(this.packageStatistics.total_energies.hgv),
      totalLibert: calculatePercentage(this.packageStatistics.total_energies.libert),
      totalObu: calculatePercentage(this.packageStatistics.total_energies.obu),
      circlekHgv: calculatePercentage(this.packageStatistics.circle_k.hgv),
      circlekLibert: calculatePercentage(this.packageStatistics.circle_k.libert),
      circlekObu: calculatePercentage(this.packageStatistics.circle_k.obu)
    }
  }

  // ⁡⁢⁣⁣➍ ⁡⁢⁣⁣𝗠𝗲𝘁𝗵𝗼𝗱𝘀⁡

  private async packagesTileStatisticsQuery() {
    this.packagesTileStatisticsLoading = true

    try {
      const result: ApolloQueryResult<PackagesTileStatisticsQuery> = await this.$apollo.query({
        query: PackagesTileStatisticsQuery
      })

      this.packageStatistics = result.data.packagesTileStatistics.packageStatistics

      this.packageItemStatistics = result.data.packagesTileStatistics.packageItemStatistics
    } catch (error) {
      console.error('[PackagesCountQuery] ERROR => ' + error)
    }

    this.packagesTileStatisticsLoading = false
  }

  private getDonutOptions(completed: number, all: number) {
    return {
      chart: {
        width: '75%',
        type: 'donut',
        animations: {
          enabled: false
        }
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      colors: ['hsl(204, 100%, 50%)', 'hsl(222, 32%, 92%)'],
      plotOptions: {
        pie: {
          customScale: 1,
          expandOnClick: false,
          donut: {
            size: '85%',
            labels: {
              show: true,
              name: {
                offsetY: 5
              },
              value: {
                show: false
              },
              total: {
                show: true,
                showAlways: true,
                label: all === -1 ? '' : `${completed}/${all}`
              }
            }
          }
        }
      },
      stroke: { show: true, width: 0 },
      tooltip: {
        enabled: false
      },
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        hover: {
          filter: {
            type: 'none',
            value: 0
          }
        },
        active: {
          filter: {
            type: 'none',
            value: 0
          }
        }
      }
    }
  }

  // ⁡⁢⁣⁣➎ 𝗪𝗮𝘁𝗰𝗵𝗲𝗿𝘀⁡

  // ⁡⁢⁣⁣➏ 𝗛𝗼𝗼𝗸𝘀⁡

  private mounted() {
    this.packagesTileStatisticsQuery()
  }
}
</script>

<style lang="scss">
  @import "@/css/chartStatisticsTile.scss";
</style>
